import { defineStore } from 'pinia';
import { useHubStore } from '~/stores/hub';
import { bytesToBase64 } from '~/util/bytes';
import { useDeviceActivityStore } from '~/stores/deviceActivity';
import { useEngineLabelsStore } from '~/stores/engineLabels';
import { forgetOpened } from '~/util/engineLabels';
import { shareKey } from '~/util/keyVault';
import { encryptionKeyOf } from '~/util/orgSigning';
/**
 * Holds each client organization's private key for the lifetime of this
 * browser tab, so unlocking once covers every device's activity and every
 * organization the user switches between, with no re-prompt on reload.
 * Backed by `sessionStorage`, not `localStorage`: it survives a reload but
 * is wiped the moment the tab closes, and never leaves this browser.
 *
 * Only each key's encryption part is stored here. Its signing part (which
 * approves devices and changes keys) stays in memory for this page load and
 * is never written here; after a reload it comes back only through the
 * person's own account key (keyVault's sealed grants), else the key is
 * asked for again before signing.
 */
const STORAGE_KEY = 'zunox-org-private-keys';
function readStoredKeys() {
    try {
        const parsed = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '{}');
        return parsed && typeof parsed === 'object' ? parsed : {};
    }
    catch {
        // Private browsing / blocked storage — fall back to prompting every time.
        return {};
    }
}
function writeStoredKeys(keys) {
    try {
        if (Object.keys(keys).length === 0)
            sessionStorage.removeItem(STORAGE_KEY);
        else
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
    }
    catch {
        // Nothing to do — the in-memory state still works for this page load.
    }
}
/** Organizations whose full key (signing part included) was unlocked in
 * this page load, as opposed to the encryption part restored from storage.
 * Only those may be shared (see `keyVault`). */
const fullKeys = new Set();
/** Drops the engine's labels opened with a client's key (every client's
 * when none is named): the results held for any later load and those a
 * page is showing. Every lock path comes through here, so no caller has to
 * remember the labels separately. */
function forgetLabels(orgId) {
    forgetOpened(orgId);
    const labels = useEngineLabelsStore();
    if (orgId === undefined || labels.orgId === orgId)
        labels.forget();
    // The decrypted activity on the pages goes too, and any decrypt still
    // running finishes into nothing.
    if (orgId === undefined || useHubStore().defaultOrganization?.id === orgId) {
        useDeviceActivityStore().clearDecrypted();
    }
}
export const useOrgKeyUnlockStore = defineStore('orgKeyUnlock', {
    state: () => ({
        keys: readStoredKeys(),
    }),
    getters: {
        /** The current organization's key, if unlocked. */
        privateKeyBase64(state) {
            const orgId = useHubStore().currentOrganizationId;
            return (orgId && state.keys[orgId]) || null;
        },
        isUnlocked() {
            return !!this.privateKeyBase64;
        },
        /** An organization's key, only if this page holds it in full. */
        heldSecret(state) {
            return (orgId) => fullKeys.has(orgId) ? state.keys[orgId] ?? null : null;
        },
        /** Whether a given organization's key is unlocked in this tab. */
        isUnlockedFor(state) {
            return (orgId) => !!state.keys[orgId];
        },
    },
    actions: {
        /** Unlocks an organization (the current one unless given). */
        unlock(privateKeyBase64, orgId = useHubStore().currentOrganizationId) {
            if (!orgId)
                return;
            const secret = privateKeyBase64.trim();
            // Parsed first: a string that isn't a key changes nothing.
            const encryptionKey = bytesToBase64(encryptionKeyOf(secret));
            // Whatever was opened under the previous key goes with it, shown or not.
            if (this.keys[orgId] && this.keys[orgId] !== secret)
                forgetLabels(orgId);
            else if (this.keys[orgId] !== secret)
                forgetOpened(orgId);
            this.keys = { ...this.keys, [orgId]: secret };
            fullKeys.add(orgId);
            writeStoredKeys({ ...readStoredKeys(), [orgId]: encryptionKey });
            // Saved to the user's account and shared with whoever may open this
            // client, so nobody pastes it again.
            void shareKey(orgId, secret);
        },
        lock(orgId = useHubStore().currentOrganizationId) {
            if (!orgId)
                return;
            const without = (keys) => Object.fromEntries(Object.entries(keys).filter(([id]) => id !== orgId));
            this.keys = without(this.keys);
            fullKeys.delete(orgId);
            forgetLabels(orgId);
            writeStoredKeys(without(readStoredKeys()));
        },
        /** Forgets every organization's key, as on signing out. */
        lockAll() {
            this.keys = {};
            forgetLabels();
            fullKeys.clear();
            writeStoredKeys({});
        },
    },
});
